import { Injectable } from "@angular/core";
import { LoadingController } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { first } from "rxjs";
import * as _ from "lodash";
import * as fromApp from "../../store/app.reducer";
import * as AuthActions from "../../auth/store/auth.actions";
import * as CurrentStoreActions from "../../store/current-store/current-store.actions";
import * as DeliveryMethodAddressActions from "../../store/deliveryMethodAddress/deliveryMethodAddress.actions";
import { DataStorageService } from "../data-storage/data-storage.service";

export type VisitorClaimOutcome = {
  success: boolean;
  comment_id?: string;
  table?: any;
};

export type DineInVisitorEligibilityOutcome = {
  eligible: boolean;
  comment_id?: string;
  table?: any;
};

@Injectable({
  providedIn: "root",
})
export class VisitorSessionAssistService {
  private readonly takeawayMode = "takeaway";
  private readonly dineInMode = "dinein";
  private readonly deliveryMode = "delivery";
  private inFlight: Promise<VisitorClaimOutcome> | null = null;
  private visitorClaimLoading: any = null;

  constructor(
    private store: Store<fromApp.AppState>,
    private dataStorageService: DataStorageService,
    private loadingController: LoadingController,
    private translateService: TranslateService
  ) {}

  isStoreEligible(storeInfo: any): boolean {
    return !!(
      storeInfo &&
      storeInfo.takeawayVisitorSystemActive === true &&
      storeInfo.store_id &&
      storeInfo.group
    );
  }

  isDineInStoreEligible(storeInfo: any): boolean {
    return !!(
      storeInfo &&
      storeInfo.dineinVisitorSystemActive === true &&
      storeInfo.store_id &&
      storeInfo.group
    );
  }

  isDeliveryStoreEligible(storeInfo: any): boolean {
    return !!(
      storeInfo &&
      storeInfo.deliveryVisitorSystemActive === true &&
      storeInfo.store_id &&
      storeInfo.group
    );
  }

  async getDineInVisitorEligibility(
    storeInfo: any
  ): Promise<DineInVisitorEligibilityOutcome> {
    if (!this.isDineInStoreEligible(storeInfo)) {
      return { eligible: false, comment_id: "dinein_visitor_system_inactive" };
    }

    const device_id = await this.getDeviceId();
    return await new Promise<DineInVisitorEligibilityOutcome>((resolve) => {
      this.dataStorageService
        .getDineInTables(storeInfo.group, storeInfo.store_id, device_id)
        .pipe(first())
        .subscribe({
          next: (res: any) => {
            if (!res || !res.success || !res.tables) {
              resolve({
                eligible: false,
                comment_id:
                  res && res.comment_id
                    ? res.comment_id
                    : "dinein_visitor_tables_unavailable",
              });
              return;
            }

            const activeTables = _.filter(res.tables, (table) => {
              return table && table.active;
            });

            if (activeTables.length !== 1) {
              resolve({
                eligible: false,
                comment_id: "dinein_visitor_requires_single_active_table",
              });
              return;
            }

            resolve({
              eligible: true,
              table: _.cloneDeep(activeTables[0]),
            });
          },
          error: () => {
            resolve({
              eligible: false,
              comment_id: "dinein_visitor_tables_network_error",
            });
          },
        });
    });
  }

  async claimDineInVisitorSession(
    storeInfo: any
  ): Promise<VisitorClaimOutcome> {
    const eligibility = await this.getDineInVisitorEligibility(storeInfo);
    if (!eligibility.eligible || !eligibility.table) {
      return {
        success: false,
        comment_id:
          eligibility.comment_id || "dinein_visitor_claim_not_eligible",
      };
    }

    const outcome = await this.claimVisitorSession(
      storeInfo,
      this.dineInMode,
      eligibility.table
    );
    if (outcome.success) {
      outcome.table = _.cloneDeep(eligibility.table);
    }

    return outcome;
  }

  async claimVisitorSession(
    storeInfo: any,
    mode = this.takeawayMode,
    table: any = null
  ): Promise<VisitorClaimOutcome> {
    if (mode === this.dineInMode) {
      if (!this.isDineInStoreEligible(storeInfo) || table?.table_id == null) {
        return {
          success: false,
          comment_id: "dinein_visitor_claim_not_eligible",
        };
      }
    } else if (mode === this.deliveryMode) {
      if (!this.isDeliveryStoreEligible(storeInfo)) {
        return {
          success: false,
          comment_id: "delivery_visitor_system_inactive",
        };
      }
    } else if (!this.isStoreEligible(storeInfo)) {
      return { success: false, comment_id: "visitor_system_inactive" };
    }

    if (this.inFlight) {
      return await this.inFlight;
    }

    this.inFlight = (async () => {
      await this.presentVisitorClaimLoading().catch(() => {});

      try {
        return await this.createVisitorSession(storeInfo, mode, table);
      } finally {
        await this.dismissVisitorClaimLoading();
      }
    })().finally(() => {
      this.inFlight = null;
    });

    return await this.inFlight;
  }

  releaseVisitorSession(visitorUser: any): void {
    // Ending the visitor session: drop the refresh marker so a later boot does
    // not wrongly clear a freshly-selected delivery method.
    try {
      window.localStorage.removeItem("mo_visitor_active");
    } catch (e) {}
    if (
      !visitorUser ||
      !visitorUser.only_visitor ||
      !visitorUser.group ||
      !visitorUser.visitor_session_id ||
      !visitorUser.visitor_slot_id
    ) {
      return;
    }

    // Fire-and-forget: a real login over a visitor session releases the old slot
    // back to the pool immediately. Failure is non-blocking; the 10-minute
    // visitor-slots cleaner is the backstop, and the backend refuses release
    // while the slot still has an active order.
    this.dataStorageService
      .releaseVisitorSession(
        visitorUser.group,
        visitorUser.visitor_session_id,
        visitorUser.visitor_slot_id
      )
      .pipe(first())
      .subscribe({
        next: () => {},
        error: () => {},
      });
  }

  /**
   * Reconcile the visitor session to the delivery mode the user just selected.
   * Fixes the silent dead-end where an already-claimed visitor changes order
   * type in the cart: the order type changed but visitor_mode did not, so
   * calculate-final-price's isVisitorDeliveryMethodConsistent blocked the final
   * price and "complete order" did nothing.
   *
   *  - real (non-visitor) user            -> untouched.
   *  - only_visitor + same mode/store     -> no-op (keep the slot).
   *  - no user + visitor-eligible target  -> fresh claim (previous behavior).
   *  - only_visitor + different mode:
   *       target visitor-eligible -> release old slot + claim new (seamless swap).
   *       target NOT eligible      -> release old slot + drop to no-login.
   *
   * Returns the claimed dine-in table when a dine-in session was (re)claimed, so
   * the caller can pin it via applyDineInDeliveryMethod; null otherwise.
   */
  async reconcileVisitorSessionForMode(
    storeInfo: any,
    targetMode: "takeaway" | "dinein" | "delivery"
  ): Promise<{ dineInTable: any }> {
    const user = await this.getAuthUser();

    // Real logged-in user: never touch their session.
    if (user && !user.only_visitor) {
      return { dineInTable: null };
    }

    const oldVisitor = user && user.only_visitor ? _.cloneDeep(user) : null;

    // A visitor session is pinned to both mode and store. Keep the slot only
    // when both still match the final target; Delivery address validation may
    // resolve to a different store while keeping the same mode.
    if (
      oldVisitor &&
      oldVisitor.visitor_mode === targetMode &&
      _.isEqual(oldVisitor.visitor_store_id, storeInfo?.store_id)
    ) {
      return { dineInTable: null };
    }

    const targetEligible =
      targetMode === "takeaway"
        ? this.isStoreEligible(storeInfo)
        : targetMode === "dinein"
        ? this.isDineInStoreEligible(storeInfo)
        : this.isDeliveryStoreEligible(storeInfo);

    // Switching modes: free the old slot first (fire-and-forget; the backend
    // refuses release while the slot still has an active order).
    if (oldVisitor) {
      this.releaseVisitorSession(oldVisitor);
    }

    if (!targetEligible) {
      // New mode cannot run as a visitor. Drop an existing
      // visitor session to a plain no-login user so the flow is not stuck on a
      // stale visitor_mode; cart and navigation are preserved.
      if (oldVisitor) {
        this.dropVisitorSession();
      }
      return { dineInTable: null };
    }

    if (targetMode === "takeaway") {
      await this.claimVisitorSession(storeInfo);
      return { dineInTable: null };
    }

    if (targetMode === "delivery") {
      const outcome = await this.claimVisitorSession(
        storeInfo,
        this.deliveryMode
      );
      if (!outcome.success && oldVisitor) {
        this.dropVisitorSession();
      }
      return { dineInTable: null };
    }

    // dinein
    const outcome = await this.claimDineInVisitorSession(storeInfo);
    if (outcome.success && outcome.table) {
      // Pin the active store to the claimed store so the catalog/cart and the
      // visitor token agree (mirrors maybeClaimDineInVisitorSession).
      if (storeInfo && storeInfo.store_id) {
        this.store.dispatch(
          new CurrentStoreActions.SetCurrentStoreId(
            _.cloneDeep(storeInfo.store_id)
          )
        );
      }
      return { dineInTable: _.cloneDeep(outcome.table) };
    }

    // Claim failed after releasing the old slot: drop to no-login rather than
    // leave a stale visitor session pointing at a released slot.
    if (oldVisitor) {
      this.dropVisitorSession();
    }
    return { dineInTable: null };
  }

  private dropVisitorSession(): void {
    // Light teardown: clear the visitor auth WITHOUT the heavy logout/erase
    // (SetLogoutFlagTimestamp -> logoutErase) so the cart and current route are
    // preserved. The user continues as a normal no-login customer.
    this.store.dispatch(new AuthActions.ClearUser());
    this.store.dispatch(new AuthActions.ClearMoToken());
    this.store.dispatch(new AuthActions.SetLoginState("no_login"));
    try {
      window.localStorage.removeItem("mo_visitor_active");
    } catch (e) {}
  }

  private async getAuthUser(): Promise<any> {
    return new Promise((resolve) => {
      this.store
        .select("auth")
        .pipe(first())
        .subscribe((state: any) =>
          resolve(state && state.user ? state.user : null)
        )
        .unsubscribe();
    });
  }

  private async createVisitorSession(
    storeInfo: any,
    mode = this.takeawayMode,
    table: any = null
  ): Promise<VisitorClaimOutcome> {
    const device_id = await this.getDeviceId();
    return await new Promise<VisitorClaimOutcome>((resolve) => {
      this.dataStorageService
        .createVisitorSession(
          storeInfo.store_id,
          storeInfo.group,
          device_id,
          mode,
          table?.table_id != null ? table.table_id : null
        )
        .subscribe({
          next: (res: any) => {
            if (
              res &&
              res.success &&
              res.token &&
              res.user &&
              res.user.only_visitor
            ) {
              this.hydrateVisitorSession(res.token, res.user);
              const outcome: VisitorClaimOutcome = { success: true };
              if (table) {
                outcome.table = _.cloneDeep(table);
              }
              resolve(outcome);
            } else {
              resolve({
                success: false,
                comment_id:
                  res && res.comment_id
                    ? res.comment_id
                    : "visitor_claim_failed",
              });
            }
          },
          error: () => {
            resolve({
              success: false,
              comment_id: "visitor_claim_network_error",
            });
          },
        });
    });
  }

  private hydrateVisitorSession(token: string, user: any) {
    // Visitor token is flow-bound, not a persistent login. We do not write to
    // localStorage.mo_token here; app.component.ts also strips any persisted
    // mo_token when currentUser.only_visitor becomes true, matching only_dinein.
    this.store.dispatch(new AuthActions.SetMoToken(token));
    if (user?.only_visitor && user?.visitor_mode === this.deliveryMode) {
      this.store.dispatch(
        new DeliveryMethodAddressActions.setSelectedPreorderDate(null)
      );
      this.store.dispatch(
        new DeliveryMethodAddressActions.setPreorderResponse(null)
      );
      try {
        const storedDeliveryMethod = JSON.parse(
          window.localStorage.getItem("deliveryMethodAddress")
        );
        if (storedDeliveryMethod) {
          storedDeliveryMethod.selectedPreorderDate = null;
          window.localStorage.setItem(
            "deliveryMethodAddress",
            JSON.stringify(storedDeliveryMethod)
          );
        }
      } catch (e) {}
    }
    this.store.dispatch(new AuthActions.SetUser(_.cloneDeep(user)));
    this.store.dispatch(new AuthActions.SetLoginState("login_completed"));
    this.store.dispatch(new AuthActions.SetLoginLoading("not-loading"));
    // Marker so that a refresh — which loses the non-persisted visitor token and
    // logs the visitor out — can detect the now-dangling visitor delivery method
    // on boot and clear it (otherwise the cart drops to a no-login "enter mobile"
    // state). Cleared on release/drop/real-login. See loadDeliveryMethodAddress.
    try {
      window.localStorage.setItem("mo_visitor_active", "true");
    } catch (e) {}
  }

  private async presentVisitorClaimLoading() {
    if (this.visitorClaimLoading) {
      return;
    }

    const message = this.translateService.instant("loading-please-wait");
    this.visitorClaimLoading = await this.loadingController.create({
      duration: 30000,
      message: message,
      cssClass: "ionicLoadingCss",
      keyboardClose: true,
    });

    return await this.visitorClaimLoading.present();
  }

  private async dismissVisitorClaimLoading() {
    if (!this.visitorClaimLoading) {
      return;
    }

    const loading = this.visitorClaimLoading;
    this.visitorClaimLoading = null;
    await loading.dismiss().catch(() => {});
  }

  private async getDeviceId(): Promise<string | null> {
    return new Promise((resolve) => {
      this.store
        .select("platformInfo")
        .pipe(first())
        .subscribe((state: any) => {
          resolve(state?.device_id || null);
        })
        .unsubscribe();
    });
  }
}
